import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface DropdownItem {
  label: string;
  path: string;
  description?: string;
  icon?: string;
}

interface NavLink {
  label: string;
  path: string;
  dropdown?: DropdownItem[];
}

const navLinks: NavLink[] = [
  { label: 'Home', path: '/' },
  {
    label: 'GCSE Resources',
    path: '/gcse/past-papers',
    dropdown: [
      { label: 'Past Papers', path: '/gcse/past-papers', description: 'Past exam papers with mark schemes', icon: 'ri-file-paper-2-line' },
      { label: 'Cheat Sheets', path: '/gcse/resources', description: 'Quick-reference revision sheets', icon: 'ri-file-list-3-line' },
      { label: 'Flash Cards', path: '/gcse/resources', description: 'Interactive active recall cards', icon: 'ri-chat-4-line' },
    ],
  },
  {
    label: 'A Level Resources',
    path: '/alevel/past-papers',
    dropdown: [
      { label: 'Past Papers', path: '/alevel/past-papers', description: 'Past exam papers with mark schemes', icon: 'ri-file-paper-2-line' },
      { label: 'Cheat Sheets', path: '/alevel/resources', description: 'Quick-reference revision sheets', icon: 'ri-file-list-3-line' },
      { label: 'Flash Cards', path: '/alevel/resources', description: 'Interactive active recall cards', icon: 'ri-chat-4-line' },
    ],
  },
  {
    label: 'Programming',
    path: '/programming',
  },
  { label: 'Events', path: '/events' },
  { label: 'Blog', path: '/blog' },
  { label: 'Contact', path: '/contact' },
];

// Pages with light/white backgrounds where transparent navbar would be invisible
const LIGHT_BG_PAGES = ['/login', '/admin', '/tutor'];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLightPage = LIGHT_BG_PAGES.some((p) => location.pathname.startsWith(p));
  const [user, setUser] = useState<{ name?: string; role?: string } | null>(() => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
    setMobileExpanded(null);
  }, [location.pathname]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('user');
      setUser(raw ? JSON.parse(raw) : null);
    } catch {
      setUser(null);
    }
    const onStorage = () => {
      try {
        const raw = localStorage.getItem('user');
        setUser(raw ? JSON.parse(raw) : null);
      } catch {
        setUser(null);
      }
    };
    window.addEventListener('storage', onStorage);
    function onDocClick(e: MouseEvent) {
      if (!userMenuRef.current) return;
      const target = e.target as Node;
      if (userMenuRef.current.contains(target)) return;
      setUserMenuOpen(false);
    }
    document.addEventListener('click', onDocClick);

    return () => {
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('click', onDocClick);
    };
  }, []);

  function initials(name?: string) {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const handleMouseEnter = (label: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(label);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || isLightPage ? 'bg-white border-b border-slate-100' : 'bg-transparent'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer flex-shrink-0 group">
            <img src="/logo.png" alt="GCSE Computing Logo" className="h-10 w-auto object-contain group-hover:scale-105 transition-transform duration-300" />
            <span
              className={`font-bold text-lg tracking-tight transition-colors duration-300 ${(scrolled || isLightPage) ? 'text-slate-900' : 'text-white'
                }`}
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              GCSE <span className={(scrolled || isLightPage) ? 'text-emerald-700' : 'text-emerald-400'}>Computing</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => link.dropdown && handleMouseEnter(link.label)}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  to={link.path}
                  className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${isActive(link.path)
                    ? (scrolled || isLightPage)
                      ? 'text-emerald-600'
                      : 'text-emerald-300'
                    : (scrolled || isLightPage)
                      ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      : 'text-white/90 hover:text-white'
                    }`}
                >
                  {link.label}
                  {link.dropdown && (
                    <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${openDropdown === link.label ? 'rotate-180' : ''}`}></i>
                  )}
                </Link>

                {link.dropdown && openDropdown === link.label && (
                  <div
                    className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl border border-slate-100 py-2 z-50"
                    style={{ boxShadow: '0 12px 32px rgba(0,0,0,0.10)' }}
                    onMouseEnter={() => handleMouseEnter(link.label)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="px-3 pb-2 mb-1 border-b border-slate-50">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{link.label}</p>
                    </div>
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.label}
                        to={item.path}
                        className="flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors cursor-pointer group"
                      >
                        {item.icon && (
                          <div className="w-7 h-7 flex items-center justify-center bg-emerald-50 rounded-lg flex-shrink-0 mt-0.5 group-hover:bg-emerald-100 transition-colors">
                            <i className={`${item.icon} text-emerald-600 text-sm`}></i>
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-slate-700 group-hover:text-slate-900">{item.label}</p>
                          {item.description && (
                            <p className="text-xs text-slate-400 mt-0.5 leading-snug">{item.description}</p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Auth Buttons / User Initials */}
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            {user && user.role !== 'admin' ? (
              <div className="relative" ref={userMenuRef}>
                <button onClick={() => setUserMenuOpen((s) => !s)} title={user.name} className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center font-semibold">{initials(user.name)}</button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded-md border border-slate-100 shadow-lg z-50 flex flex-col py-1">
                    {user.role === 'tutor' && (
                      <Link
                        to='/tutor/dashboard'
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Dashboard
                      </Link>
                    )}
                    {user.role === 'student' && (
                      <Link
                        to='/student/dashboard'
                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Dashboard
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        setUser(null);
                        setUserMenuOpen(false);
                        window.location.href = '/';
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-rose-600 hover:bg-slate-50 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap px-3 py-2 rounded-md ${(scrolled || isLightPage) ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-50' : 'text-white/90 hover:text-white'
                    }`}
                >
                  Log in
                </Link>
                <Link
                  to="/login?tab=register"
                  className="text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Register Free
                </Link>
              </>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${(scrolled || isLightPage) ? 'text-slate-700' : 'text-white'
              }`}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-slate-100 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <div key={link.label}>
                {link.dropdown ? (
                  <>
                    <button
                      onClick={() => setMobileExpanded(mobileExpanded === link.label ? null : link.label)}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer"
                    >
                      <span>{link.label}</span>
                      <i className={`ri-arrow-down-s-line text-sm transition-transform ${mobileExpanded === link.label ? 'rotate-180' : ''}`}></i>
                    </button>
                    {mobileExpanded === link.label && (
                      <div className="pl-3 mt-1 flex flex-col gap-0.5">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.label}
                            to={item.path}
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer"
                          >
                            {item.icon && <i className={`${item.icon} text-emerald-500 text-sm`}></i>}
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.path}
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer"
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
            <div className="flex gap-3 mt-3 pt-3 border-t border-slate-100">
              {user && user.role !== 'admin' ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-3 w-full">
                  <div title={user.name} className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-semibold text-lg">{initials(user.name)}</div>
                  {user.role === 'tutor' && (
                    <Link
                      to='/tutor/dashboard'
                      onClick={() => setMobileOpen(false)}
                      className="text-sm font-medium text-slate-700 bg-slate-50 border border-slate-200 py-2 rounded-lg w-full text-center"
                    >
                      Dashboard
                    </Link>
                  )}
                  {user.role === 'student' && (
                    <Link
                      to='/student/dashboard'
                      onClick={() => setMobileOpen(false)}
                      className="text-sm font-medium text-slate-700 bg-slate-50 border border-slate-200 py-2 rounded-lg w-full text-center"
                    >
                      Dashboard
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      localStorage.removeItem('token');
                      localStorage.removeItem('user');
                      setUser(null);
                      setMobileOpen(false);
                      window.location.href = '/';
                    }}
                    className="text-sm font-medium text-rose-600 bg-rose-50 py-2 rounded-lg w-full text-center"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="flex-1 text-center text-sm font-medium text-slate-600 border border-slate-200 py-2 rounded-lg cursor-pointer whitespace-nowrap"
                  >
                    Log in
                  </Link>
                  <Link
                    to="/login?tab=register"
                    className="flex-1 text-center text-sm font-medium bg-emerald-600 text-white py-2 rounded-lg cursor-pointer whitespace-nowrap"
                  >
                    Register Free
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
